import React, { useState, useEffect } from 'react';

import { useMultipleRealtimeSubscriptions } from '../../hooks/useRealtimeSubscription';
import { format } from 'date-fns';
import { Activity, AlertTriangle, Database, Mail, CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import AdminSidebar from '../../components/ui/AdminSidebar';
import MetricsCard from '../admin-dashboard/components/MetricsCard';
import {
  getApplicationStatusMetrics,
  getErrorFrequencyTrends,
  getDatabasePerformanceMetrics,
  getEmailDeliveryHealth,
  getSystemHealthOverview,
  detectCriticalFailures
} from '../../services/monitoringService';
import { fetchSecurityAlerts } from '../../services/alertNotificationService';

const MonitoringDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState(24); // hours

  // Metrics state
  const [applicationMetrics, setApplicationMetrics] = useState(null);
  const [errorTrends, setErrorTrends] = useState([]);
  const [databasePerformance, setDatabasePerformance] = useState(null);
  const [emailHealth, setEmailHealth] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [criticalAlerts, setCriticalAlerts] = useState([]);

  useEffect(() => {
    fetchMonitoringData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchMonitoringData(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [timeRange]);

  // Real-time subscriptions for monitoring data
  useMultipleRealtimeSubscriptions([
    {
      table: 'applications',
      onInsert: () => fetchMonitoringData(true),
      onUpdate: () => fetchMonitoringData(true),
      onDelete: () => fetchMonitoringData(true)
    },
    {
      table: 'error_logs',
      onInsert: () => {
        console.log('[Monitoring] New error logged');
        fetchMonitoringData(true);
        // Check for critical failures
        detectCriticalFailures();
      }
    },
    {
      table: 'email_delivery_logs',
      onInsert: () => fetchMonitoringData(true),
      onUpdate: () => fetchMonitoringData(true)
    },
    {
      table: 'security_alerts',
      onInsert: (newAlert) => {
        console.log('[Monitoring] New alert created:', newAlert);
        fetchCriticalAlerts();
      },
      onUpdate: () => fetchCriticalAlerts()
    }
  ]);

  const fetchMonitoringData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Fetch all monitoring data in parallel
      const [appMetrics, errorData, dbPerformance, emailData, healthOverview] = await Promise.all([
        getApplicationStatusMetrics(),
        getErrorFrequencyTrends(timeRange),
        getDatabasePerformanceMetrics(timeRange),
        getEmailDeliveryHealth(timeRange),
        getSystemHealthOverview()
      ]);

      setApplicationMetrics(appMetrics);
      setErrorTrends(errorData || []);
      setDatabasePerformance(dbPerformance);
      setEmailHealth(emailData);
      setSystemHealth(healthOverview);

      // Fetch critical alerts
      await fetchCriticalAlerts();
    } catch (error) {
      console.error('Error fetching monitoring data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchCriticalAlerts = async () => {
    try {
      const { data } = await fetchSecurityAlerts({
        status: 'new',
        priority: 'critical'
      });
      setCriticalAlerts(data || []);
    } catch (error) {
      console.error('Error fetching critical alerts:', error);
    }
  };

  const handleRefresh = () => {
    fetchMonitoringData(true);
  };

  // Format error trends data for chart
  const formattedErrorTrends = errorTrends?.slice(0, 12)?.reverse()?.map(item => ({
    time: format(new Date(item?.hour_bucket), 'HH:mm'),
    errors: parseInt(item?.error_count) || 0,
    critical: parseInt(item?.critical_count) || 0,
    high: parseInt(item?.high_count) || 0
  }));

  // Email delivery status distribution
  const emailStatusData = emailHealth ? [
    { name: 'Erfolgreich', value: parseInt(emailHealth?.sent_count) || 0, color: '#10b981' },
    { name: 'Fehlgeschlagen', value: parseInt(emailHealth?.failed_count) || 0, color: '#ef4444' }
  ] : [];

  // System health status color
  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-success';
      case 'warning': return 'text-warning';
      case 'critical': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getHealthStatusBg = (status) => {
    switch (status) {
      case 'healthy': return 'bg-success/10';
      case 'warning': return 'bg-warning/10';
      case 'critical': return 'bg-error/10';
      default: return 'bg-muted';
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-background overflow-hidden">
        <AdminSidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <RefreshCw className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Lade Monitoring-Daten...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <AdminSidebar />
      <div className="flex-1 overflow-y-auto overflow-x-hidden lg:ml-60">
        <div className="p-4 sm:p-6 w-full max-w-[1600px] mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-2 break-words">
                System-Monitoring
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground break-words">
                Echtzeit-Überwachung von Anwendungsstatus, Fehlertrends, Datenbankleistung und E-Mail-Zustellung
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(parseInt(e?.target?.value))}
                className="px-3 sm:px-4 py-2 border border-border rounded-lg bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value={1}>Letzte Stunde</option>
                <option value={6}>Letzte 6 Stunden</option>
                <option value={24}>Letzte 24 Stunden</option>
                <option value={72}>Letzte 3 Tage</option>
                <option value={168}>Letzte Woche</option>
              </select>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-primary text-primary-foreground text-sm rounded-lg hover:bg-primary/90 transition-smooth disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Aktualisieren</span>
              </button>
            </div>
          </div>

          {/* System Health Overview */}
          {systemHealth && (
            <div className={`mb-6 p-4 rounded-lg border ${getHealthStatusBg(systemHealth?.overallStatus)} border-border`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start gap-3">
                  <Activity className={`w-6 h-6 flex-shrink-0 ${getHealthStatusColor(systemHealth?.overallStatus)}`} />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-foreground break-words">
                      Systemstatus: <span className={getHealthStatusColor(systemHealth?.overallStatus)}>
                        {systemHealth?.overallStatus === 'healthy' ? 'Gesund' : 
                         systemHealth?.overallStatus === 'warning' ? 'Warnung' : 'Kritisch'}
                      </span>
                    </h3>
                    {systemHealth?.issues?.length > 0 && (
                      <p className="text-sm text-muted-foreground mt-1 break-words">
                        {systemHealth?.issues?.join(', ')}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
                  Letzte Aktualisierung: {format(new Date(), 'HH:mm:ss')}
                </div>
              </div>
            </div>
          )}

          {/* Critical Alerts */}
          {criticalAlerts?.length > 0 && (
            <div className="mb-6 p-4 bg-error/10 border border-error rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-error flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-foreground mb-2">
                    Kritische Warnungen ({criticalAlerts?.length})
                  </h3>
                  <div className="space-y-2">
                    {criticalAlerts?.slice(0, 3)?.map(alert => (
                      <div key={alert?.id} className="bg-card p-3 rounded border border-border">
                        <p className="font-medium text-foreground text-sm break-words">{alert?.title}</p>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-1 break-words">{alert?.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {format(new Date(alert?.created_at), 'dd.MM.yyyy HH:mm')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Application Status Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
            <MetricsCard
              title="Gesamtanträge"
              value={applicationMetrics?.total || 0}
              subtitle={`${applicationMetrics?.todayApplications || 0} heute`}
              icon="FileText"
              color="primary"
              trend="up"
              trendValue={0}
            />
            <MetricsCard
              title="Ausstehend"
              value={applicationMetrics?.pending || 0}
              subtitle="Warten auf Bearbeitung"
              icon="Clock"
              color="warning"
              trend="neutral"
              trendValue={0}
            />
            <MetricsCard
              title="Genehmigt"
              value={applicationMetrics?.approved || 0}
              subtitle={`${applicationMetrics?.approvalRate || 0}% Genehmigungsrate`}
              icon="CheckCircle"
              color="success"
              trend="up"
              trendValue={0}
            />
            <MetricsCard
              title="Abgelehnt"
              value={applicationMetrics?.rejected || 0}
              subtitle="Nicht genehmigt"
              icon="XCircle"
              color="secondary"
              trend="neutral"
              trendValue={0}
            />
          </div>

          {/* Error Frequency Trends */}
          <div className="bg-card rounded-lg shadow-card p-4 sm:p-6 border border-border mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-1">Fehlertrends</h3>
                <p className="text-xs sm:text-sm text-muted-foreground break-words">
                  Fehlerfrequenz über die letzten {timeRange} Stunden
                </p>
              </div>
              <AlertTriangle className="w-6 h-6 text-warning flex-shrink-0" />
            </div>
            {formattedErrorTrends?.length > 0 ? (
              <div className="w-full overflow-hidden">
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={formattedErrorTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="time" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line
                      type="monotone"
                      dataKey="errors"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      name="Alle Fehler"
                    />
                    <Line
                      type="monotone"
                      dataKey="critical"
                      stroke="#ef4444"
                      strokeWidth={2}
                      name="Kritisch"
                    />
                    <Line
                      type="monotone"
                      dataKey="high"
                      stroke="#f97316"
                      strokeWidth={2}
                      name="Hoch"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground text-sm">
                Keine Fehlerdaten verfügbar
              </div>
            )}
          </div>

          {/* Database Performance & Email Health */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
            {/* Database Performance */}
            <div className="bg-card rounded-lg shadow-card p-4 sm:p-6 border border-border">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-1">Datenbankleistung</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground break-words">
                    Abfrageleistung und Metriken
                  </p>
                </div>
                <Database className="w-6 h-6 text-primary flex-shrink-0" />
              </div>
              {databasePerformance ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg gap-2">
                    <span className="text-xs sm:text-sm font-medium text-foreground break-words">Durchschn. Abfragezeit</span>
                    <span className="text-base sm:text-lg font-bold text-primary whitespace-nowrap">
                      {parseFloat(databasePerformance?.avg_query_time_ms || 0)?.toFixed(2)} ms
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg gap-2">
                    <span className="text-xs sm:text-sm font-medium text-foreground break-words">Max. Abfragezeit</span>
                    <span className="text-base sm:text-lg font-bold text-warning whitespace-nowrap">
                      {parseFloat(databasePerformance?.max_query_time_ms || 0)?.toFixed(2)} ms
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg gap-2">
                    <span className="text-xs sm:text-sm font-medium text-foreground break-words">Langsame Abfragen</span>
                    <span className="text-base sm:text-lg font-bold text-error whitespace-nowrap">
                      {parseInt(databasePerformance?.slow_query_count) || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg gap-2">
                    <span className="text-xs sm:text-sm font-medium text-foreground break-words">Abfragen/Minute</span>
                    <span className="text-base sm:text-lg font-bold text-foreground whitespace-nowrap">
                      {parseFloat(databasePerformance?.queries_per_minute || 0)?.toFixed(2)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground text-sm">
                  Keine Leistungsdaten verfügbar
                </div>
              )}
            </div>

            {/* Email Delivery Health */}
            <div className="bg-card rounded-lg shadow-card p-4 sm:p-6 border border-border">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-1">E-Mail-Zustellung</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground break-words">
                    Erfolgs- und Fehlerquoten
                  </p>
                </div>
                <Mail className="w-6 h-6 text-primary flex-shrink-0" />
              </div>
              {emailHealth ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg gap-2">
                    <span className="text-xs sm:text-sm font-medium text-foreground break-words">Gesamt E-Mails</span>
                    <span className="text-base sm:text-lg font-bold text-foreground whitespace-nowrap">
                      {parseInt(emailHealth?.total_emails) || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-success/10 rounded-lg gap-2">
                    <span className="text-xs sm:text-sm font-medium text-foreground break-words">Erfolgsrate</span>
                    <span className="text-base sm:text-lg font-bold text-success whitespace-nowrap">
                      {parseFloat(emailHealth?.success_rate || 0)?.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-error/10 rounded-lg gap-2">
                    <span className="text-xs sm:text-sm font-medium text-foreground break-words">Fehlerrate</span>
                    <span className="text-base sm:text-lg font-bold text-error whitespace-nowrap">
                      {parseFloat(emailHealth?.failure_rate || 0)?.toFixed(1)}%
                    </span>
                  </div>
                  {emailStatusData?.length > 0 && (
                    <div className="mt-4 w-full overflow-hidden">
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie
                            data={emailStatusData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value }) => `${name}: ${value}`}
                            outerRadius={70}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {emailStatusData?.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry?.color} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ fontSize: '12px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground text-sm">
                  Keine E-Mail-Daten verfügbar
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonitoringDashboard;